import {
  NINA_EVIDENCE_STATUSES,
  NINA_MEMORY_CATEGORIES,
  NINA_MEMORY_SCHEMA_VERSION,
  NINA_RISK_LEVELS,
} from "./NinaMemorySchema";

const SECRET_FIELD_NAMES = [
  "password",
  "passphrase",
  "secret",
  "token",
  "credential",
  "apikey",
  "privatekey",
  "authorization",
  "auth",
];

const isSecretFieldName = (key) => {
  const compactKey = String(key).replace(/[^a-z0-9]/gi, "").toLowerCase();
  return SECRET_FIELD_NAMES.some(
    (name) =>
      compactKey === name ||
      (name !== "auth" &&
        (compactKey.endsWith(name) || compactKey.startsWith(name)))
  );
};

const cleanText = (value, maxLength = 4000) =>
  typeof value === "string" ? value.trim().slice(0, maxLength) : "";

const containsSecretField = (value) => {
  if (!value || typeof value !== "object") return false;

  return Object.entries(value).some(([key, child]) => {
    if (isSecretFieldName(key)) return true;
    return child && typeof child === "object"
      ? containsSecretField(child)
      : false;
  });
};

const normalizeSymbol = (symbol) => {
  if (symbol === null || symbol === undefined || symbol === "") return "";
  return cleanText(symbol, 12).toUpperCase();
};

const isIsoTimestamp = (value) =>
  typeof value === "string" &&
  Number.isFinite(Date.parse(value)) &&
  new Date(value).toISOString() === value;

export const validateMemoryRecord = (input = {}) => {
  const errors = [];

  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { valid: false, errors: ["Record must be an object."] };
  }

  if (containsSecretField(input)) {
    errors.push("Credential- or secret-like fields are not allowed.");
  }

  if (!NINA_MEMORY_CATEGORIES.includes(input.category)) {
    errors.push("Unsupported memory category.");
  }

  if (!NINA_EVIDENCE_STATUSES.includes(input.evidenceStatus)) {
    errors.push("Unsupported evidence status.");
  }

  if (!NINA_RISK_LEVELS.includes(input.riskLevel || "none")) {
    errors.push("Unsupported risk level.");
  }

  if (!cleanText(input.source, 120)) {
    errors.push("Source is required.");
  }

  if (!isIsoTimestamp(input.timestamp)) {
    errors.push("Timestamp must be an ISO timestamp.");
  }

  const symbol = normalizeSymbol(input.symbol);
  if (symbol && !/^[A-Z][A-Z0-9.-]{0,11}$/.test(symbol)) {
    errors.push("Symbol format is invalid.");
  }

  if (
    input.confidence !== null &&
    input.confidence !== undefined &&
    (!Number.isFinite(Number(input.confidence)) ||
      Number(input.confidence) < 0 ||
      Number(input.confidence) > 100)
  ) {
    errors.push("Confidence must be between 0 and 100.");
  }

  if (
    input.category === "user-approved-rule" &&
    input.userApproved !== true
  ) {
    errors.push("User-approved rules require explicit userApproved: true.");
  }

  if (
    !cleanText(input.title, 180) &&
    !cleanText(input.details) &&
    !cleanText(input.outcome, 1000) &&
    !cleanText(input.lesson, 1000)
  ) {
    errors.push("A title, details, outcome, or lesson is required.");
  }

  return { valid: errors.length === 0, errors };
};

export const normalizeMemoryRecord = (input, now = new Date().toISOString()) => ({
  schemaVersion: NINA_MEMORY_SCHEMA_VERSION,
  timestamp: input.timestamp || now,
  symbol: normalizeSymbol(input.symbol),
  category: input.category,
  source: cleanText(input.source, 120),
  sourceRecordId: cleanText(input.sourceRecordId, 240) || null,
  evidenceStatus: input.evidenceStatus,
  confidence:
    input.confidence === null || input.confidence === undefined
      ? null
      : Number(input.confidence),
  riskLevel: input.riskLevel || "none",
  title: cleanText(input.title, 180),
  details: cleanText(input.details),
  outcome: cleanText(input.outcome, 1000),
  lesson: cleanText(input.lesson, 1000),
  tags: Array.isArray(input.tags)
    ? [...new Set(input.tags.map((tag) => cleanText(tag, 40)).filter(Boolean))]
    : [],
  userApproved: input.userApproved === true,
});

const simpleHash = (value) => {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
};

export const createMemoryFingerprint = (record) => {
  if (record.sourceRecordId) {
    return `source:${record.source}:${record.sourceRecordId}`;
  }

  const normalized = [
    record.category,
    record.symbol || "",
    record.source,
    record.evidenceStatus,
    record.confidence ?? "",
    record.riskLevel,
    record.title.toLowerCase(),
    record.details.toLowerCase(),
    record.outcome.toLowerCase(),
    record.lesson.toLowerCase(),
    record.tags.join(","),
    record.userApproved ? "approved" : "not-approved",
  ].join("|");

  return `fingerprint:${simpleHash(normalized)}`;
};

export const areDuplicateMemoryRecords = (candidate, existing) => {
  if (candidate.sourceRecordId || existing.sourceRecordId) {
    return Boolean(
      candidate.sourceRecordId &&
        existing.sourceRecordId &&
        candidate.source === existing.source &&
        candidate.sourceRecordId === existing.sourceRecordId
    );
  }

  if (
    createMemoryFingerprint(candidate) !== createMemoryFingerprint(existing)
  ) {
    return false;
  }

  return (
    Math.abs(Date.parse(candidate.timestamp) - Date.parse(existing.timestamp)) <=
    30000
  );
};

export const hasSecretLikeFields = containsSecretField;
